import React from 'react';
import styled, { css } from 'styled-components';
import { Label, NonCopyableMarkdown, SectionWrapper } from '../common';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
} from '../../../contexts/assessment-test';
import FollowUp from './FollowUp';

type GapContextType = {
  getValue: (id: string) => string;
  setValue: (id: string, value: string) => void;
  getPlaceholder: (id: string) => string;
};

const GapContext = React.createContext<GapContextType>({
  getValue: () => '',
  setValue: () => {},
  getPlaceholder: (id: string) => id,
});

export default function FillInTheGapSection({
  section,
  stepId,
}: {
  section: Extract<
    AssessmentTestContextType['steps'][number]['sections'][number],
    { type: 'fill-in-the-gap' }
  >;
  stepId: string;
}) {
  const { blanks, answer, content } = section;
  const [, dispatch] = React.useContext(AssessmentTestContext);
  const getBlankPlaceholder = React.useCallback(
    (id: string) =>
      blanks.find((b) => b.id === id)?.placeholder ||
      blanks.find((b) => b.id === id)?.content ||
      id,
    [blanks]
  );

  const getValue = React.useCallback(
    (id: string) => answer.find((a) => a.id === id)?.content || '',
    [answer]
  );

  const setAnswer = React.useCallback(
    (value: Array<{ id: string; content: string }>) => {
      dispatch({
        type: 'CHANGE_ANSWER_FILL_IN_THE_GAP',
        payload: { stepId, sectionId: section.id, answer: value },
      });
    },
    [stepId, section.id, dispatch]
  );

  const setValue = React.useCallback(
    (id: string, value: string) => {
      const exists = answer.some((a) => a.id === id);
      if (exists) {
        setAnswer(
          answer.map((a) => (a.id === id ? { ...a, content: value } : a))
        );
      } else {
        setAnswer([...answer, { id, content: value }]);
      }
    },
    [answer, setAnswer]
  );

  const components = React.useMemo(() => {
    const renderWithPlaceholders = (
      Tag: keyof JSX.IntrinsicElements,
      isCode: boolean
    ) =>
      function Renderer(
        props: Omit<React.HTMLAttributes<HTMLElement>, 'onCopy'> & {
          inline?: boolean;
          className?: string;
        }
      ) {
        const gap = React.useContext(GapContext);
        const children = props.children as unknown as Array<React.ReactNode>;
        return (
          <Tag {...(props as unknown as Record<string, unknown>)}>
            {children?.map((child, idx) =>
              typeof child === 'string' ? (
                <React.Fragment key={idx}>
                  {splitByPlaceholders(child).map((part, pidx) =>
                    part.type === 'text' ? (
                      <React.Fragment key={pidx}>{part.value}</React.Fragment>
                    ) : (
                      <FillBlank
                        key={`${part.id}-${pidx}`}
                        id={part.id}
                        value={gap.getValue(part.id)}
                        onChange={(v) => gap.setValue(part.id, v)}
                        placeholder={gap.getPlaceholder(part.id)}
                        isCode={isCode}
                      />
                    )
                  )}
                </React.Fragment>
              ) : (
                <React.Fragment key={idx}>{child}</React.Fragment>
              )
            )}
          </Tag>
        );
      };

    const built: Record<string, React.ComponentType<Record<string, unknown>>> =
      {
        // Block code and inline code composed with syntax highlighting
        code: function CodeRenderer(props: {
          inline?: boolean;
          className?: string;
          children?: React.ReactNode;
        }) {
          const gap = React.useContext(GapContext);
          const { inline, className } = props;
          const langMatch = /language-(\w+)/.exec(className || '') ?? [];
          const language = langMatch ? langMatch[1] : undefined;
          const text = String(props.children ?? '');
          const parts = splitByPlaceholders(text);

          if (inline) {
            return (
              <code className={className} style={{ whiteSpace: 'pre' }}>
                {parts.map((part, idx) =>
                  part.type === 'text' ? (
                    <span key={idx}>{part.value}</span>
                  ) : (
                    <FillBlank
                      key={`${part.id}-${idx}`}
                      id={part.id}
                      value={gap.getValue(part.id)}
                      onChange={(v) => gap.setValue(part.id, v)}
                      placeholder={gap.getPlaceholder(part.id)}
                      isCode
                    />
                  )
                )}
              </code>
            );
          }

          return (
            <CodeBlockWrapper>
              {parts.map((part, idx) =>
                part.type === 'text' ? (
                  <InlineCodeSpan key={idx}>
                    <SyntaxHighlighter
                      language={language || 'text'}
                      style={atomOneDark}
                      PreTag="span"
                      CodeTag="span"
                      customStyle={{
                        display: 'inline',
                        background: 'transparent',
                        whiteSpace: 'pre-wrap',
                        padding: 0,
                        margin: 0,
                      }}
                      wrapLongLines
                    >
                      {part.value}
                    </SyntaxHighlighter>
                  </InlineCodeSpan>
                ) : (
                  <FillBlank
                    key={`${part.id}-${idx}`}
                    id={part.id}
                    value={gap.getValue(part.id)}
                    onChange={(v) => gap.setValue(part.id, v)}
                    placeholder={gap.getPlaceholder(part.id)}
                    isCode
                  />
                )
              )}
            </CodeBlockWrapper>
          );
        },

        // Common block/inline containers that may contain placeholders
        p: renderWithPlaceholders('p', false),
        li: renderWithPlaceholders('li', false),
        span: renderWithPlaceholders('span', false),
        strong: renderWithPlaceholders('strong', false),
        em: renderWithPlaceholders('em', false),
        h1: renderWithPlaceholders('h1', false),
        h2: renderWithPlaceholders('h2', false),
        h3: renderWithPlaceholders('h3', false),
        h4: renderWithPlaceholders('h4', false),
        h5: renderWithPlaceholders('h5', false),
        h6: renderWithPlaceholders('h6', false),
        blockquote: renderWithPlaceholders('blockquote', false),
        td: renderWithPlaceholders('td', false),
        th: renderWithPlaceholders('th', false),
      };

    return built;
  }, []);

  const ctx = React.useMemo<GapContextType>(
    () => ({ getValue, setValue, getPlaceholder: getBlankPlaceholder }),
    [getValue, setValue, getBlankPlaceholder]
  );

  return (
    <SectionWrapper>
      <GapContext.Provider value={ctx}>
        <NonCopyableMarkdown md={content} components={components} />
      </GapContext.Provider>
      <Label>Type in your answer in each of the gaps.</Label>
      <FollowUp section={section} stepId={stepId} />
    </SectionWrapper>
  );
}

function splitByPlaceholders(
  text: string
): Array<{ type: 'text'; value: string } | { type: 'blank'; id: string }> {
  const result: Array<
    { type: 'text'; value: string } | { type: 'blank'; id: string }
  > = [];
  const regex = /\[([a-zA-Z0-9_-]+)\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const [full, id] = match;
    const idx = match.index;
    if (idx > lastIndex) {
      result.push({ type: 'text', value: text.slice(lastIndex, idx) });
    }
    result.push({ type: 'blank', id });
    lastIndex = idx + full.length;
  }
  if (lastIndex < text.length) {
    result.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return result;
}

const sharedBlankStyles = css`
  display: inline-block;
  padding: 4px 6px;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  vertical-align: middle;
  margin-bottom: 2px;
`;

const CodeBlankWrapper = styled.span`
  ${sharedBlankStyles}
  font-size: 15px;
  font-family: 'Roboto Mono', monospace;
  line-height: 20px;
  white-space: nowrap;
  vertical-align: middle;
`;

const TextBlankWrapper = styled.span`
  ${sharedBlankStyles}
  font-size: 15px;
  font-family: 'Roboto Mono', monospace;
  line-height: 20px;
  white-space: nowrap;
  vertical-align: middle;
`;

const BlankInput = styled.input<{ $isCode?: boolean }>`
  border: none;
  outline: none;
  background: transparent;
  color: inherit;
  font: inherit;
  min-width: 1sch;
  max-width: 50ch;
  padding: 0;
  padding-left: 4px;
  margin-top: 0px;
  font-size: 15px;
`;

const CodeBlockWrapper = styled.pre`
  background: ${() => {
    const theme = atomOneDark as unknown as {
      hljs?: { background?: string; color?: string };
    };
    return theme.hljs?.background || '#282c34';
  }};
  color: ${() => {
    const theme = atomOneDark as unknown as {
      hljs?: { background?: string; color?: string };
    };
    return theme.hljs?.color || '#abb2bf';
  }};
  border-radius: 8px;
  padding: 16px;
  white-space: pre-wrap;
  word-break: normal;
  overflow-x: auto;
  display: block;
  gap: 0px;
  font-size: 15px;
  font-family: 'Roboto Mono', monospace;
  margin: 0;
  padding-left: 16px;
`;

// Ensures we can nest input spans directly alongside highlighted text inside the same line box
const InlineCodeSpan = styled.span`
  display: inline;
  padding: 0;
  margin: 0;
`;

function FillBlank({
  id,
  value,
  onChange,
  placeholder,
  isCode,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  isCode?: boolean;
}) {
  const Wrapper = isCode ? CodeBlankWrapper : TextBlankWrapper;
  const size = Math.max(
    1,
    Math.min(60, Math.max((placeholder || id).length, value.length))
  );
  return (
    <Wrapper data-blank-id={id}>
      <BlankInput
        aria-label={id}
        value={value}
        placeholder={placeholder || id}
        onChange={(e) => onChange(e.target.value)}
        $isCode={!!isCode}
        size={size}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
      />
    </Wrapper>
  );
}
